import React from 'react';
import { cn } from '../../utils/cn';

interface AlertProps {
  type?: 'error' | 'success' | 'warning' | 'info';
  children: React.ReactNode;
  className?: string;
}

interface AlertDescriptionProps {
  children: React.ReactNode;
  className?: string;
}

export function Alert({ type = 'info', children, className }: AlertProps) {
  const baseStyles = "p-4 rounded-lg border text-sm mb-4";
  const typeStyles = {
    error: "bg-destructive/10 border-destructive/20 text-destructive",
    success: "bg-success/10 border-success/20 text-success",
    warning: "bg-warning/10 border-warning/20 text-warning",
    info: "bg-info/10 border-info/20 text-info"
  };

  const roleMap = {
    error: 'alert',
    success: 'status',
    warning: 'alert',
    info: 'status'
  };

  const ariaLiveMap = {
    error: 'assertive' as const,
    success: 'polite' as const,
    warning: 'polite' as const,
    info: 'polite' as const
  };

  return (
    <div 
      className={cn(baseStyles, typeStyles[type], "animate-fadeInDown", className)}
      role={roleMap[type]}
      aria-live={ariaLiveMap[type]}
    >
      <div className="flex">
        {type === 'error' && (
          <svg className="h-5 w-5 text-destructive mr-2" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        )}
        {type === 'success' && (
          <svg className="h-5 w-5 text-success mr-2" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        )}
        {type === 'warning' && (
          <svg className="h-5 w-5 text-warning mr-2" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        )}
        {type === 'info' && (
          <svg className="h-5 w-5 text-info mr-2" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        )}
        <div>{children}</div>
      </div>
    </div>
  );
}

export function AlertDescription({ children, className }: AlertDescriptionProps) {
  return <div className={cn("text-sm", className)}>{children}</div>;
}
